import { createRoot } from "react-dom/client";
import { App as CapApp } from "@capacitor/app";
import { StatusBar, Style } from "@capacitor/status-bar";
import App from "./App";
import { Auth0Wrapper } from "./components/Auth0Wrapper";
import { checkAndInvalidate } from "@/lib/digestClient";
import { toaster } from "@/lib/toaster";
import { isNativeMobile } from "@/utils/capacitor";
// Self-hosted fonts — avoids FOUT from Google Fonts CDN dependency
import "@fontsource/jost/latin-700.css";
import "@fontsource/jost/latin-800.css";
import "@fontsource/dm-sans/latin-300.css";
import "@fontsource/dm-sans/latin-400.css";
import "@fontsource/dm-sans/latin-500.css";
import "@fontsource/dm-sans/latin-600.css";
import "./index.css";

// Home restores its own scroll position (keyed by the active search) once
// results have rendered. Opt out of the browser's native scroll restoration so
// the two don't fight on Back.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

if (isNativeMobile()) {
  // Match the splash screen background so there's no colour jump when the WebView loads.
  document.documentElement.style.backgroundColor = '#FDFDFB';
  StatusBar.setStyle({ style: Style.Dark });
  StatusBar.setBackgroundColor({ color: '#FDFDFB' });
}

// Handle Android hardware back button and edge swipe gestures.
// Without this, Capacitor exits the app instead of navigating back.
CapApp.addListener("backButton", ({ canGoBack }) => {
  if (canGoBack) {
    window.history.back();
  } else {
    CapApp.exitApp();
  }
});

// Handle camera/photo picker results interrupted by Android killing the
// Activity in the background. The original Camera.getPhoto() promise is dead
// in this case — prompt the user to try again.
CapApp.addListener("appRestoredResult", (data) => {
  if (!data.success) {
    toaster.create({
      title: "Photo selection was interrupted",
      description: "Please try again.",
      type: "error",
    });
  }
});

// Timestamp of when the app was last backgrounded.
let backgroundedAt: number | null = null;
const BACKGROUND_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

// Check the digest on foreground and invalidate only what changed.
// For brief app switches (<5 min) we skip the check entirely.
CapApp.addListener("appStateChange", ({ isActive }) => {
  if (!isActive) {
    backgroundedAt = Date.now();
    return;
  }

  const elapsed = backgroundedAt !== null ? Date.now() - backgroundedAt : Infinity;
  backgroundedAt = null;

  if (elapsed < BACKGROUND_THRESHOLD_MS) return;

  void checkAndInvalidate();
});

createRoot(document.getElementById("root")!).render(
  <Auth0Wrapper>
    <App />
  </Auth0Wrapper>
);
